import type { ThreadMessageLike } from "@assistant-ui/react";
import { toListItem } from "@circle-react-shared/AiConversation/composer/Attachments/attachmentHelpers";
import type { AttachmentItem } from "@circle-react-shared/AiConversation/composer/Attachments/types";
import { threadMessageId } from "@circle-react-shared/AiConversation/message/threadMessageId";
import type {
  Message,
  Source,
  Step,
} from "@circle-react/types/CommunityInbox/Message";
import type { CommunityMember } from "@circle-react/types/CommunityMember";
import type { JSONContent } from "@tiptap/core";

import { didAiPauseChange } from "@/react/components/SettingsApp/CommunityInbox/BotChatRoom/ChatWindow/helper";

// A Tiptap document can't go in AUI's text part, so it travels as a data part.
export const TIPTAP_BODY_PART = "tiptap_body";

export interface AvatarMessageMetadata {
  adminAuthor?: CommunityMember;
  aiPauseChange?: boolean;
  attachments?: AttachmentItem[];
  sources?: Source[];
  steps?: Step[];
  tiptap_body?: JSONContent;
}

export const toAvatarThreadMessages = (
  messages: Message[],
): ThreadMessageLike[] =>
  messages.map((message, index) => {
    const id = threadMessageId(message.id);
    const createdAt = new Date(message.created_at);
    const isUserMessage = message.author_type === "community_member";

    // Empty content lets the indicator stand in until the first streamed snapshot arrives.
    if (!isUserMessage && message.is_processing) {
      return {
        role: "assistant" as const,
        content: message.streaming_text
          ? [{ type: "text" as const, text: message.streaming_text }]
          : [],
        id,
        createdAt,
        status: { type: "running" as const },
      };
    }

    const content = [
      {
        type: "data" as const,
        name: TIPTAP_BODY_PART,
        data: { ...message.body, attachments: [] },
      },
    ];

    const previousMessage = messages[index - 1];
    const hasAiPauseChanged = didAiPauseChange(
      Boolean(previousMessage?.ai_snoozed),
      message.ai_snoozed,
    );

    const attachments = message.body?.attachments?.map(
      ({ signed_id, ...attachment }) =>
        toListItem({ ...attachment, id: signed_id }),
    );

    const custom = {
      // Duplicated into the slot the shared copy action reads.
      tiptap_body: {
        ...(message.body?.body ?? {}),
        sgids_to_object_map: message.body?.sgids_to_object_map ?? {},
      },
      ...(message.sent_by_admin && message.community_member
        ? { adminAuthor: message.community_member }
        : {}),
      // The new pause state, set only on the message a pause/resume divider is shown above.
      aiPauseChange: hasAiPauseChanged ? message.ai_snoozed : undefined,
      ...(attachments?.length ? { attachments } : {}),
      ...(message.sources?.length ? { sources: message.sources } : {}),
      ...(message.steps?.length ? { steps: message.steps } : {}),
    };

    return {
      role: isUserMessage ? ("user" as const) : ("assistant" as const),
      content,
      id,
      createdAt,
      metadata: { custom },
    };
  });
